import type { IDrag } from "./DragAndDropContext";
import { useDragAndDropContext } from "./DragAndDropContext";
import { isDraggedItemRoutable } from "./dragAndDropUtils";
import { useIsShiftDropZoneAvailable } from "./useIsShiftDropZoneAvailable";

/**
 * The shift drop zone grows to cover the whole shift when nothing inside the shift can
 * take the dragged item: a routable item when the shift has no routes yet, and a
 * non-routable item always.
 */
export const shouldEnlargeShiftDropZone = ({
  drag,
  isShiftDropZoneAvailable,
  hasRoutes,
}: {
  drag: IDrag | undefined;
  isShiftDropZoneAvailable: boolean;
  hasRoutes: boolean;
}): boolean => {
  if (!drag || !isShiftDropZoneAvailable) {
    return false;
  }

  return !isDraggedItemRoutable(drag) || !hasRoutes;
};

// The enlarged drop zone is anchored to its shift (CSS anchor positioning), which is
// newly supported across browsers. Where it isn't, the drop zone keeps its usual size.
const canEnlargeDropZones = () =>
  // oxlint-disable-next-line lingui/no-unlocalized-strings
  CSS.supports("anchor-name: --a") && CSS.supports("anchor-scope: --a");

export const useIsShiftDropZoneEnlarged = ({
  shiftId,
  hasRoutes,
}: {
  shiftId: number;
  hasRoutes: boolean;
}) => {
  const { drag } = useDragAndDropContext();
  const isShiftDropZoneAvailable = useIsShiftDropZoneAvailable({ shiftId });

  return (
    canEnlargeDropZones() &&
    shouldEnlargeShiftDropZone({ drag, isShiftDropZoneAvailable, hasRoutes })
  );
};
